
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { checkPasswordStrength, getStrengthColor, getStrengthTextColor, PasswordStrengthResult } from '@/utils/passwordStrength';

interface PasswordStrengthInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onStrengthChange?: (result: PasswordStrengthResult) => void;
  showWeakPasswordError?: boolean;
}

export const PasswordStrengthInput: React.FC<PasswordStrengthInputProps> = ({
  value,
  onChange,
  placeholder = "Create a secure password",
  className = "",
  onStrengthChange,
  showWeakPasswordError = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [strengthResult, setStrengthResult] = useState<PasswordStrengthResult | null>(null);

  useEffect(() => {
    if (value) {
      const result = checkPasswordStrength(value);
      setStrengthResult(result);
      onStrengthChange?.(result);
    } else {
      setStrengthResult(null);
      onStrengthChange?.(null);
    }
  }, [value, onStrengthChange]);

  const getProgressBars = () => {
    if (!strengthResult || !value) return [];
    
    const { score } = strengthResult;
    const bars = [];
    const numBars = 3; // Like Stripe
    const filledBars = Math.ceil((score / 100) * numBars);
    
    for (let i = 0; i < numBars; i++) {
      const isFilled = i < filledBars;
      bars.push(
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
            isFilled ? 'bg-green-500' : 'bg-gray-200'
          }`}
        />
      );
    }
    
    return bars;
  };

  const requirements = strengthResult ? strengthResult.requirements : checkPasswordStrength('').requirements;

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`appearance-none rounded-md relative block w-full px-3 py-2 pr-20 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm ${className}`}
        />
        
        {/* Help Icon */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute inset-y-0 right-8 flex items-center px-1 text-gray-400 hover:text-gray-600"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Password requirements:</p>
              <div className="space-y-1">
                {requirements.map((req) => (
                  <div key={req.id} className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${req.met ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className={`text-xs ${req.met ? 'text-green-700' : 'text-gray-600'}`}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Show/Hide Password Icon */}
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Progress bars and strength indicator - only show when there's text */}
      {value && strengthResult && (
        <div className="space-y-2">
          <div className="flex space-x-1">
            {getProgressBars()}
          </div>
          
          {strengthResult.level === 'Strong' && (
            <div className="flex justify-end">
              <span className="text-sm font-medium text-green-600">Strong</span>
            </div>
          )}
        </div>
      )}

      {/* Error message for weak password submission attempts */}
      {showWeakPasswordError && strengthResult && !strengthResult.isValid && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-700 mb-2">
            <strong>Password too weak.</strong> Please create a stronger password to continue.
          </p>
          <div className="space-y-1">
            {requirements.filter(req => !req.met).map((req) => (
              <div key={req.id} className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-xs text-red-600">{req.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
